import { useMemo, useRef, useState } from 'react';
import { Badge } from 'antd';
import { useTranslation } from 'react-i18next';
import { Col } from 'antd';

import PriceDisplay from 'components/_local/PriceDisplay/PriceDisplay';
import ImagePlaceholder from 'images/no-image-placeholder.jpg';

import { encodeImageUrl } from 'utils/general';

import {
  CardBodyRow,
  CoverPhoto,
  ProductAvatar,
  ProductLabel,
  KeywordLabel,
  OriPriceLabel,
  PriceAndQuantityContainer,
  PriceLabel,
  StyledCard,
  ProductTag,
  InfoContainer
} from './ProductCard.styles';

const ProductPriceDisplay = ({ isRange, minPrice, maxPrice }) => {
  return isRange ? (
    <>
      <PriceDisplay amount={minPrice} />
      <span> - </span>
      <PriceDisplay amount={maxPrice} />
    </>
  ) : (
    <PriceDisplay amount={minPrice} />
  );
};

const ProductCard = ({
  coverPhotoUrl,
  listingPhotoUrls,
  name,
  keyword,
  priceMax,
  priceMin,
  oriPriceMin,
  oriPriceMax,
  onClick,
  isActive = true,
  hasStock,
  quantityLeft,
  isLiveSelling,
  cardColor
}) => {
  const { t } = useTranslation(['compProductCard']);
  const hasPriceRange = priceMax !== priceMin;
  const hasOriPriceRange = oriPriceMax !== oriPriceMin;
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const pointerStateRef = useRef({ isPointerDown: false, startX: 0, startY: 0, swipeHandled: false });
  const suppressClickUntilRef = useRef(0);

  const previewPhotoUrls = useMemo(() => {
    const validListingPhotoUrls = (listingPhotoUrls || []).filter(Boolean);

    if (!coverPhotoUrl) {
      return validListingPhotoUrls.slice(0, 2);
    }

    if (validListingPhotoUrls.length === 0) {
      return [coverPhotoUrl];
    }

    if (validListingPhotoUrls[0] === coverPhotoUrl) {
      return validListingPhotoUrls.slice(0, 2);
    }

    const remainingUrls = validListingPhotoUrls.filter(url => url !== coverPhotoUrl);
    return [coverPhotoUrl, ...remainingUrls].slice(0, 2);
  }, [coverPhotoUrl, listingPhotoUrls]);

  const hasPreviewPhoto = previewPhotoUrls.length > 1;
  const activePhotoUrl = previewPhotoUrls[activePhotoIndex] || previewPhotoUrls[0] || coverPhotoUrl || ImagePlaceholder;

  const handleCardClick = () => {
    if (Date.now() < suppressClickUntilRef.current) {
      return;
    }

    if (isActive && onClick) {
      onClick();
    }
  };

  const handlePointerDown = event => {
    if (!hasPreviewPhoto || !isActive) {
      return;
    }

    pointerStateRef.current = {
      isPointerDown: true,
      startX: event.clientX,
      startY: event.clientY,
      swipeHandled: false
    };
  };

  const handlePointerMove = event => {
    if (!hasPreviewPhoto || !isActive || !pointerStateRef.current.isPointerDown || pointerStateRef.current.swipeHandled) {
      return;
    }

    const deltaX = event.clientX - pointerStateRef.current.startX;
    const deltaY = event.clientY - pointerStateRef.current.startY;
    const isHorizontalSwipe = Math.abs(deltaX) > 24 && Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe) {
      setActivePhotoIndex(currentIndex => (currentIndex === 0 ? 1 : 0));
      pointerStateRef.current.swipeHandled = true;
      suppressClickUntilRef.current = Date.now() + 350;
    }
  };

  const handlePointerUp = () => {
    pointerStateRef.current.isPointerDown = false;
  };

  const handlePointerLeave = () => {
    pointerStateRef.current.isPointerDown = false;
  };

  const handleMouseEnter = () => {
    if (hasPreviewPhoto && isActive) {
      setActivePhotoIndex(1);
    }
  };

  const handleMouseLeave = () => {
    setActivePhotoIndex(0);
  };

  return (
    <StyledCard
      isActive={isActive}
      hoverable={isActive && !!onClick}
      cover={
        !isLiveSelling && (
          <Badge.Ribbon text={t('compProductCard:out-of-stock-label')} color="#9b9b9b" style={{ display: hasStock ? 'none' : 'auto' }}>
            <CoverPhoto src={encodeImageUrl(activePhotoUrl)} />
          </Badge.Ribbon>
        )
      }
      styles={{ body: { padding: 0 } }}
      isLiveSelling={isLiveSelling}
      cardColor={cardColor}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleCardClick}
    >
      <CardBodyRow gutter={8} wrap={false} isLiveSelling={isLiveSelling} align="middle">
        {isLiveSelling && (
          <Col>
            <ProductAvatar shape="square" src={encodeImageUrl(activePhotoUrl)}></ProductAvatar>
          </Col>
        )}
        <Col flex="auto" style={{ height: '100%' }}>
          <InfoContainer>
            <div>
              <ProductLabel hasOriPrice={!!oriPriceMin} isLiveSelling={isLiveSelling} ellipsis={{ rows: 2 }}>
                {name}
              </ProductLabel>
              <KeywordLabel hasOriPrice={!!oriPriceMin} isLiveSelling={isLiveSelling} ellipsis={{ rows: 1 }}>
                {keyword}
              </KeywordLabel>
            </div>
            <PriceAndQuantityContainer>
              <div style={{ textAlign: 'center', width: '100%' }}>
                <PriceLabel hasOriPrice={!!oriPriceMin} ellipsis={{ rows: 2 }}>
                  <ProductPriceDisplay isRange={hasPriceRange} minPrice={priceMin} maxPrice={priceMax} />
                </PriceLabel>
                {!!oriPriceMin && (
                  <OriPriceLabel ellipsis={{ rows: 2 }}>
                    <ProductPriceDisplay isRange={hasOriPriceRange} minPrice={oriPriceMin} maxPrice={oriPriceMax} />
                  </OriPriceLabel>
                )}
              </div>
              {isActive ? (
                <div>
                  {isLiveSelling && quantityLeft < 10 && (
                    <ProductTag highlight={hasStock}>
                      {hasStock ? t('compProductCard:inventory-left-label', { inventory: quantityLeft }) : t('compProductCard:out-of-stock-label')}
                    </ProductTag>
                  )}
                </div>
              ) : (
                <div>
                  <ProductTag>{t('compProductCard:inactive-label')}</ProductTag>
                </div>
              )}
            </PriceAndQuantityContainer>
          </InfoContainer>
        </Col>
      </CardBodyRow>
    </StyledCard>
  );
};

export default ProductCard;
